import { useState, useEffect, useCallback } from 'react';

const _normalizeApiBase = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith(':')) {
    return `${window.location.protocol}//${window.location.hostname}${raw}`;
  }
  if (raw.startsWith('//')) {
    return `${window.location.protocol}${raw}`;
  }
  if (raw.startsWith('/')) {
    return `${window.location.origin}${raw}`;
  }
  return raw.replace(/\/+$/, '');
};

const API_BASE =
  _normalizeApiBase((import.meta as any).env?.VITE_API_BASE || '') ||
  _normalizeApiBase((window as any).__API_BASE__ || '') ||
  window.location.origin;

export interface AccessibleUser {
  id: number;
  username: string;
  full_name: string;
  email: string;
  role: string;
  role_level: 'system_admin' | 'ho_admin' | 'site_admin' | 'general_user';
  is_self: boolean;
}

interface UseRoleBasedUserFilterResult {
  /** All users that the current user may view data for */
  accessibleUsers: AccessibleUser[];
  /** The currently selected user ID (undefined = viewing own data only) */
  selectedUserId: number | undefined;
  setSelectedUserId: (id: number | undefined) => void;
  /** True when the viewer is allowed to filter by other users */
  canFilterUsers: boolean;
  /** Role level of the logged-in viewer */
  viewerRole: string;
  isLoading: boolean;
}

const getAuthHeaders = (): Record<string, string> => {
  const token = sessionStorage.getItem('access_token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeRole = (role?: string | null): string => {
  const value = String(role || '').trim().toLowerCase().replace(' ', '_');
  if (!value) return 'general_user';
  if (value === 'company_admin') return 'ho_admin';
  if (value === 'user' || value === 'viewer') return 'general_user';
  return value;
};

export function useRoleBasedUserFilter(siteCode?: string): UseRoleBasedUserFilterResult {
  const [accessibleUsers, setAccessibleUsers] = useState<AccessibleUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [viewerRole, setViewerRole] = useState<string>(normalizeRole(sessionStorage.getItem('role_name') || sessionStorage.getItem('user_role')));
  const [isLoading, setIsLoading] = useState(true);

  const fetchVisible = useCallback(async (requestedSiteCode?: string) => {
    setIsLoading(true);
    try {
      const qs = new URLSearchParams();
      const normalizedSite = String(requestedSiteCode || '').trim().toUpperCase();
      if (normalizedSite) qs.append('site_code', normalizedSite);
      const endpoint = `${API_BASE}/api/v1/fulfillment/simulation-visible-users${qs.toString() ? `?${qs.toString()}` : ''}`;

      const res = await fetch(
        endpoint,
        {
          headers: {
            ...getAuthHeaders(),
            ...(normalizedSite ? { 'X-Site-Code': normalizedSite } : {}),
          }
        }
      );
      if (res.ok) {
        const data = await res.json();
        setAccessibleUsers(data.users || []);
        setViewerRole(normalizeRole(data.viewer_role || 'general_user'));
        // Auto-select self
        const self = (data.users as AccessibleUser[]).find((u) => u.is_self);
        if (self) {
          setSelectedUserId(self.id);
        } else {
          const fallbackUserId = Number(sessionStorage.getItem('user_id') || 0);
          if (fallbackUserId > 0) setSelectedUserId(fallbackUserId);
        }
      }
    } catch {
      // Backend unavailable – default to single-user mode
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisible(siteCode);
  }, [fetchVisible, siteCode]);

  const canFilterUsers =
    viewerRole !== 'general_user' && accessibleUsers.length > 1;

  return {
    accessibleUsers,
    selectedUserId,
    setSelectedUserId,
    canFilterUsers,
    viewerRole,
    isLoading,
  };
}
